import type {
  Category,
  HeroBanner,
  Product,
  ProductBadge,
  ProductSummary,
} from '../types'
import { placeholderBanner, placeholderImage } from './placeholder'

/**
 * Dados de demonstração.
 *
 * Nada aqui vai para produção: com `VITE_USE_MOCKS=false` este módulo nem é
 * alcançado. Serve para desenvolver e revisar o layout com conteúdo realista.
 */

export const categories: Category[] = [
  { id: 'cat-eletronicos', name: 'Eletrônicos, Áudio e Vídeo', slug: 'eletronicos' },
  { id: 'cat-esportes', name: 'Esportes e Fitness', slug: 'esportes-e-fitness' },
  { id: 'cat-beleza', name: 'Beleza e Cuidado Pessoal', slug: 'beleza-e-cuidado-pessoal' },
  { id: 'cat-casa', name: 'Casa, Móveis e Decoração', slug: 'casa-moveis-e-decoracao' },
  { id: 'cat-moda', name: 'Calçados, Roupas e Bolsas', slug: 'calcados-roupas-e-bolsas' },
  { id: 'cat-ferramentas', name: 'Ferramentas', slug: 'ferramentas' },
  { id: 'cat-instrumentos', name: 'Instrumentos Musicais', slug: 'instrumentos-musicais' },
  { id: 'cat-informatica', name: 'Informática', slug: 'informatica' },
]

interface Seed {
  id: string
  title: string
  categoryId: string
  price: number
  discountPercent?: number
  badges?: ProductBadge[]
  soldCount?: number
  rating?: number
}

const seeds: Seed[] = [
  // Eletrônicos
  { id: 'p-fone-usbc', title: 'Fones De Ouvido Usb-c Tipo C Com Microfone Integrado Som Estéreo', categoryId: 'cat-eletronicos', price: 23.77, discountPercent: 7, badges: ['mais_vendido'], soldCount: 1240, rating: 4.5 },
  { id: 'p-projetor', title: 'Projetor Smart Portátil Full Hd 4k Android 11 Wi-fi Bluetooth Mini Cinema', categoryId: 'cat-eletronicos', price: 359.9, discountPercent: 9, badges: ['mais_vendido'], soldCount: 860, rating: 4.4 },
  { id: 'p-echo-dot', title: 'Echo Dot 5a Geração Alexa Amazon Smart Speaker Com Som Aprimorado', categoryId: 'cat-eletronicos', price: 445.23, discountPercent: 3, soldCount: 5200, rating: 4.8 },
  { id: 'p-tv-philco-32', title: 'Smart Tv 32 Philco P32vik Roku Led Dolby Audio Hdr Wi-fi Bivolt', categoryId: 'cat-eletronicos', price: 919.08, discountPercent: 8, badges: ['mais_vendido'], soldCount: 430, rating: 4.3 },
  { id: 'p-antena', title: 'Antena De Tv Interna Uhf/hdtv Intelbras Ai 2025 Alto Ganho', categoryId: 'cat-eletronicos', price: 32.2, discountPercent: 2, soldCount: 2100, rating: 4.2 },
  { id: 'p-tv-aoc-43', title: 'Smart Tv 43 Aoc Led Roku Full Hd Wi-fi 60hz Hdmi Usb 43s5135/78g', categoryId: 'cat-eletronicos', price: 1376, discountPercent: 6, badges: ['mais_vendido'], soldCount: 310, rating: 4.6 },
  { id: 'p-tv-lg-50', title: 'Smart Tv LG 50\'\' 4K Ultra HD NANO AI Magic Webos 26 50nano66', categoryId: 'cat-eletronicos', price: 2224, discountPercent: 4, badges: ['mais_vendido'], soldCount: 180, rating: 4.7 },
  { id: 'p-tv-tcl-43', title: 'Smart TV TCL 43 Polegadas Full HD QLED S5K WiFi Bluetooth Google TV', categoryId: 'cat-eletronicos', price: 1929, discountPercent: 9, badges: ['mais_vendido'], soldCount: 640, rating: 4.5 },
  { id: 'p-tv-roku-50', title: 'Smart Tv Roku Tv 50 7045 4k Uhd Aoc Bivolt Dolby Vision', categoryId: 'cat-eletronicos', price: 1950, badges: ['mais_vendido'], soldCount: 220, rating: 4.4 },
  { id: 'p-tv-multi-43', title: 'Smart Tv Dled 43 Fhd Multi Roku 3hdmi 2usb Wi-fi Tl056m', categoryId: 'cat-eletronicos', price: 1994, discountPercent: 4, badges: ['mais_buscado'], soldCount: 95, rating: 4.1 },

  // Esportes e fitness
  { id: 'p-creatina', title: 'Creatina Monohidratada Pura 1kg Dark Lab Unidade Sem sabor', categoryId: 'cat-esportes', price: 70.9, discountPercent: 9, badges: ['mais_vendido'], soldCount: 8900, rating: 4.9 },
  { id: 'p-compressor', title: 'Mini Compressor Digital Portátil Rezzet para Carro Moto e Bicicleta', categoryId: 'cat-esportes', price: 67.66, discountPercent: 6, soldCount: 3400, rating: 4.6 },
  { id: 'p-jogger', title: 'Kit 2 Calças Jogger Tactel Com Elastano Impulse', categoryId: 'cat-esportes', price: 113.71, discountPercent: 1, soldCount: 720, rating: 4.3 },
  { id: 'p-bike', title: 'Bicicleta Aro 29 Aço Carbono Freios A Disco Suspensão 21 Vel', categoryId: 'cat-esportes', price: 740.03, discountPercent: 3, badges: ['mais_vendido'], soldCount: 260, rating: 4.4 },
  { id: 'p-garrafa', title: 'Garrafa térmica Termolar Lúmina Bomba de Pressão 1L', categoryId: 'cat-esportes', price: 73.99, discountPercent: 9, soldCount: 1800, rating: 4.7 },

  // Beleza
  { id: 'p-natura-luna', title: 'Natura Luna Força Deo Colônia Feminina 75 ml', categoryId: 'cat-beleza', price: 84.65, discountPercent: 5, soldCount: 1500, rating: 4.8 },
  { id: 'p-maquina-corte', title: 'Máquina De Acabamento Corta Cabelo Profissional Kemei 2299 Bivolt', categoryId: 'cat-beleza', price: 61.23, discountPercent: 3, badges: ['mais_vendido'], soldCount: 4300, rating: 4.5 },
  { id: 'p-serum-vitc', title: 'Principia Sérum Vitamina C-10 Facial Antioxidante 30ml', categoryId: 'cat-beleza', price: 40, soldCount: 2600, rating: 4.7 },
  { id: 'p-wella', title: 'Kit Wella Professionals Invigo Nutri-enrich Salon Duo', categoryId: 'cat-beleza', price: 280.92, discountPercent: 2, soldCount: 340, rating: 4.6 },
  { id: 'p-escova-rotativa', title: 'Escova Rotativa Ber21pd Biv Preto E Dourado Bivolt', categoryId: 'cat-beleza', price: 189.7, discountPercent: 7, soldCount: 910, rating: 4.4 },

  // Ofertas gerais
  { id: 'p-carregador-40w', title: 'Carregador Turbo 40w Compativel Com iPhone 8 ao 16 Cabo Incluso', categoryId: 'cat-informatica', price: 33, discountPercent: 3, soldCount: 6700, rating: 4.5 },
  { id: 'p-talheres', title: 'Jogo Talheres Faqueiro Búzios Aço Inox 24 Peças Tramontina', categoryId: 'cat-casa', price: 61.96, discountPercent: 6, soldCount: 1130, rating: 4.7 },
  { id: 'p-cameras-ip', title: 'Kit 2 Câmeras Segurança Ip Interna Externa Wifi iCSee Infravermelho', categoryId: 'cat-eletronicos', price: 180.99, discountPercent: 9, badges: ['oferta_do_dia'], soldCount: 2400, rating: 4.5 },
  { id: 'p-espumas-jbl', title: 'Espumas Fone Compatível Jbl Tune600 T450 T450bt T500bt T500 Preto', categoryId: 'cat-eletronicos', price: 33.9, soldCount: 780, rating: 4.6 },
  { id: 'p-furadeira', title: 'Parafusadeira Furadeira 12v Bivolt Com Maleta E 30 Acessórios', categoryId: 'cat-ferramentas', price: 219.9, discountPercent: 12, badges: ['mais_vendido'], soldCount: 1450, rating: 4.5 },
  { id: 'p-teclado-mec', title: 'Teclado Mecânico Gamer Rgb Abnt2 Switch Blue Anti-ghosting', categoryId: 'cat-informatica', price: 149.9, discountPercent: 15, badges: ['mais_buscado'], soldCount: 3200, rating: 4.4 },
]

function buildProduct(seed: Seed): Product {
  const originalPrice = seed.discountPercent
    ? Math.round((seed.price / (1 - seed.discountPercent / 100)) * 100) / 100
    : null

  const category = categories.find((c) => c.id === seed.categoryId) ?? null

  return {
    id: seed.id,
    slug: seed.id.replace(/^p-/, ''),
    title: seed.title,
    categoryId: category?.id ?? null,
    categoryName: category?.name ?? null,
    price: seed.price,
    originalPrice,
    discountPercent: seed.discountPercent ?? null,
    images: [
      placeholderImage(seed.id, seed.title),
      placeholderImage(`${seed.id}-2`, seed.title),
      placeholderImage(`${seed.id}-3`, seed.title),
    ],
    rating: seed.rating ?? null,
    ratingCount: seed.rating ? Math.round((seed.soldCount ?? 100) * 0.12) : null,
    soldCount: seed.soldCount ?? null,
    availableQuantity: 100,
    badges: seed.badges ?? [],
    externalUrl: `https://produto.mercadolivre.com.br/${seed.id}`,
    deliveryDescription: 'Chega grátis amanhã. Frete grátis em compras acima de R$ 79.',
    highlights: [
      'Garantia de 3 meses direto com o vendedor',
      'Produto novo, na caixa lacrada',
      'Envio para todo o Brasil',
    ],
    specifications: [
      {
        title: 'Características gerais',
        items: [
          { label: 'Marca', value: 'Genérica' },
          { label: 'Modelo', value: seed.id.toUpperCase() },
          { label: 'Condição', value: 'Novo' },
        ],
      },
      {
        title: 'Dimensões e peso',
        items: [
          { label: 'Peso', value: '1,2 kg' },
          { label: 'Altura', value: '18 cm' },
          { label: 'Largura', value: '24 cm' },
        ],
      },
    ],
    description:
      'Produto listado a partir de uma loja parceira. Ao finalizar a compra no site indicado, ' +
      'volte aqui e marque o item como comprado para gerar seu cupom da semana.',
    source: 'api',
    createdAt: '2026-06-01T12:00:00.000Z',
    updatedAt: '2026-07-20T12:00:00.000Z',
  }
}

export const products: Product[] = seeds.map(buildProduct)

export function toSummary(product: Product): ProductSummary {
  return {
    id: product.id,
    slug: product.slug,
    title: product.title,
    price: product.price,
    originalPrice: product.originalPrice,
    discountPercent: product.discountPercent,
    images: product.images,
    badges: product.badges,
    soldCount: product.soldCount,
    categoryName: product.categoryName,
    externalUrl: product.externalUrl,
  }
}

export const banners: HeroBanner[] = [
  {
    id: 'b-ofertas',
    imageUrl: placeholderBanner('OFERTAS SELECIONADAS', 'Prêmios em dinheiro toda semana'),
    alt: 'Ofertas selecionadas — prêmios em dinheiro toda semana',
    href: '/busca',
  },
  {
    id: 'b-eletronicos',
    imageUrl: placeholderBanner('SEMANA DA TECNOLOGIA', 'Eletrônicos com até 40% OFF'),
    alt: 'Semana da tecnologia — eletrônicos com até 40% de desconto',
    href: '/categoria/eletronicos',
  },
  {
    id: 'b-casa',
    imageUrl: placeholderBanner('CASA E DECORAÇÃO', 'Renove seus ambientes gastando pouco'),
    alt: 'Casa e decoração — renove seus ambientes gastando pouco',
    href: '/categoria/casa-moveis-e-decoracao',
  },
]

/** Mensagens rotativas da tarja amarela. */
export const promoMessages = [
  { id: 'm1', icon: '🤑', text: 'Prêmios em dinheiro toda semana!' },
  { id: 'm2', icon: '📣', text: 'Cada compra gera um cupom para concorrer a prêmios' },
]
